// src/pages/TrafegoMarketing.js (Implementação com visualizações avançadas)
import React, { useContext, useState, useEffect } from 'react';
import { MarketingContext } from '../store/marketingContext';

// Componentes originais que serão melhorados ou substituídos
import TrafficForm from '../components/Marketing/TrafficForm';
import TrafficTable from '../components/Marketing/TrafficTable';
import ChannelMetrics from '../components/Marketing/ChannelMetrics';
import ROICalculator from '../components/Marketing/ROICalculator';

// Componentes de visualização avançada
import {
  KpiCard,
  KpiCardGroup,
  MetricChart,
  PerformanceBar,
  InsightGenerator,
  DistributionPie,
  ScatterAnalysis,
  MetricTable
} from '../components/Visualization';

// Hook personalizado para dados
import useVisualizationData from '../hooks/useVisualizationData';

// Utilitários
import { formatNumber } from '../utils/dataUtils';

// Ícones
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Mail,
  ShoppingBag,
  Plus,
  Filter,
  Calendar,
  BarChart2,
  PieChart,
  RefreshCw,
  Download,
  Target,
  Edit,
  Trash2,
  Eye,
  AlertTriangle,
  HelpCircle,
  Activity
} from 'lucide-react';

const TrafegoMarketing = () => {
  // Estados para controle da interface
  const [activeTab, setActiveTab] = useState('dashboard');
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');
  const [channelFilter, setChannelFilter] = useState('all');
  const [showInsights, setShowInsights] = useState(true);

  // Contexto original de marketing (mantendo compatibilidade)
  const {
    campaigns = [],
    channelMetrics = {},
    totalInvestment = 0,
    totalRevenue = 0,
    totalROI = 0,
    totalLeads = 0,
    totalSales = 0,
    cpa = 0,
    addCampaign = () => {},
    removeCampaign = () => {},
    updateCampaign = () => {}
  } = useContext(MarketingContext) || {};

  // Manipuladores de eventos originais
  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setActiveTab('campaigns');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingCampaign(null);
  };

  const handleSaveEdit = (updatedCampaign) => {
    if (editingCampaign && editingCampaign.id) {
      updateCampaign(editingCampaign.id, updatedCampaign);
      setEditingCampaign(null);
    }
  };

  const handleAddCampaign = (campaign) => {
    addCampaign(campaign);
  };

  const handleRemoveCampaign = (id) => {
    removeCampaign(id);
    if (editingCampaign?.id === id) {
      setEditingCampaign(null);
    }
  };

  // Hooks para visualizações avançadas
  const {
    data: campaignData,
    loading: campaignLoading,
    insights: campaignInsights,
    summary: campaignSummary,
    refresh: refreshCampaigns
  } = useVisualizationData({
    dataType: 'marketing',
    timeRange,
    metrics: [
      { key: 'cost', label: 'Investimento', aggregation: 'sum', higherIsBetter: false },
      { key: 'revenue', label: 'Receita', aggregation: 'sum', higherIsBetter: true },
      { key: 'roi', label: 'ROI', aggregation: 'avg', higherIsBetter: true, detectAnomalies: true },
      { key: 'cpa', label: 'CPA', aggregation: 'avg', higherIsBetter: false, detectAnomalies: true },
      { key: 'clicks', label: 'Cliques', aggregation: 'sum', higherIsBetter: true },
      { key: 'conversions', label: 'Conversões', aggregation: 'sum', higherIsBetter: true }
    ],
    filters: channelFilter !== 'all' ? { channel: channelFilter } : {},
    generateInsights: true,
    detectAnomalies: true
  });

  // Dados agrupados por canal
  const {
    data: channelData,
    summary: channelSummary
  } = useVisualizationData({
    dataType: 'marketing',
    timeRange,
    metrics: [
      { key: 'cost', label: 'Investimento', aggregation: 'sum' },
      { key: 'revenue', label: 'Receita', aggregation: 'sum' },
      { key: 'roi', label: 'ROI', aggregation: 'avg' },
      { key: 'cpa', label: 'CPA', aggregation: 'avg', higherIsBetter: false },
      { key: 'clicks', label: 'Cliques', aggregation: 'sum' },
      { key: 'conversions', label: 'Conversões', aggregation: 'sum' }
    ],
    groupBy: 'channel'
  });

  // Dados de tendência diária
  const {
    data: dailyData
  } = useVisualizationData({
    dataType: 'marketing',
    timeRange,
    metrics: [
      { key: 'cost', label: 'Investimento', aggregation: 'sum' },
      { key: 'revenue', label: 'Receita', aggregation: 'sum' },
      { key: 'roi', label: 'ROI', aggregation: 'avg' }
    ],
    groupBy: 'date'
  });

  // Preparar dados para KPIs principais
  const kpiMetrics = [
    {
      title: 'Investimento',
      value: totalInvestment,
      prefix: 'R$ ',
      change: 8.4, // Idealmente calculado a partir dos dados
      timeframe: 'vs. período anterior',
      color: 'purple',
      trendData: dailyData.slice(-10),
      trendKey: 'cost',
      inverseTrend: true,
      icon: <DollarSign className="h-5 w-5 text-purple-600" />
    },
    {
      title: 'Receita',
      value: totalRevenue,
      prefix: 'R$ ',
      change: 12.5,
      timeframe: 'vs. período anterior',
      color: 'blue',
      trendData: dailyData.slice(-10),
      trendKey: 'revenue',
      icon: <TrendingUp className="h-5 w-5 text-blue-600" />
    },
    {
      title: 'ROI',
      value: totalROI,
      suffix: '%',
      change: 5.2,
      timeframe: 'vs. período anterior',
      color: 'green',
      trendData: dailyData.slice(-10),
      trendKey: 'roi',
      icon: <Target className="h-5 w-5 text-green-600" />
    },
    {
      title: 'CPA',
      value: cpa,
      prefix: 'R$ ',
      change: -3.1,
      timeframe: 'vs. período anterior',
      color: 'orange',
      trendData: dailyData.slice(-10),
      trendKey: 'cpa',
      inverseTrend: true,
      icon: <Users className="h-5 w-5 text-orange-600" />
    },
    {
      title: 'Leads',
      value: totalLeads,
      change: 9.8,
      timeframe: 'vs. período anterior',
      color: 'indigo',
      icon: <Mail className="h-5 w-5 text-indigo-600" />
    },
    {
      title: 'Vendas',
      value: totalSales,
      change: 7.2,
      timeframe: 'vs. período anterior',
      color: 'red',
      icon: <ShoppingBag className="h-5 w-5 text-red-600" />
    }
  ];

  // Configuração para gráfico de tendência diária
  const dailyTrendConfig = {
    data: dailyData,
    series: [
      {
        type: 'line',
        dataKey: 'cost',
        name: 'Investimento',
        color: '#A855F7',
        valueFormatter: (value) => `R$ ${value.toLocaleString()}`
      },
      {
        type: 'line',
        dataKey: 'revenue',
        name: 'Receita',
        color: '#3B82F6',
        valueFormatter: (value) => `R$ ${value.toLocaleString()}`
      },
      {
        type: 'line',
        dataKey: 'roi',
        name: 'ROI',
        color: '#10B981',
        valueFormatter: (value) => `${value.toFixed(1)}%`,
        yAxisId: 'right'
      }
    ],
    xAxisDataKey: 'date',
    title: 'Tendência de Performance',
    subtitle: 'Evolução diária de investimento, receita e ROI',
    height: 400,
    showControls: true
  };

  // Configuração para gráfico de performance por canal
  const channelPerformanceConfig = {
    data: channelData,
    series: [
      {
        type: 'bar',
        dataKey: 'cost',
        name: 'Investimento',
        color: '#A855F7',
        valueFormatter: (value) => `R$ ${value.toLocaleString()}`
      },
      {
        type: 'bar',
        dataKey: 'revenue',
        name: 'Receita',
        color: '#3B82F6',
        valueFormatter: (value) => `R$ ${value.toLocaleString()}`
      },
      {
        type: 'line',
        dataKey: 'roi',
        name: 'ROI',
        color: '#10B981',
        valueFormatter: (value) => `${value.toFixed(1)}%`,
        yAxisId: 'right'
      }
    ],
    xAxisDataKey: 'channel',
    title: 'Performance por Canal',
    subtitle: 'Investimento, receita e ROI por canal de marketing',
    height: 350
  };

  // Distribuição de investimento por canal
  const channelDistributionData = channelData.map(channel => ({
    name: channel.channel,
    value: channel.cost
  }));

  // Configuração para tabela de campanhas
  const campaignTableColumns = [
    {
      key: 'name',
      label: 'Campanha',
      sortable: true
    },
    {
      key: 'channel',
      label: 'Canal',
      sortable: true,
      type: 'tag',
      tagColors: {
        'facebook': 'bg-blue-100 text-blue-800',
        'google': 'bg-yellow-100 text-yellow-800',
        'tiktok': 'bg-black text-white',
        'taboola': 'bg-indigo-100 text-indigo-800'
      }
    },
    {
      key: 'cost',
      label: 'Investimento',
      sortable: true,
      type: 'number',
      format: (value) => `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
    },
    {
      key: 'clicks',
      label: 'Cliques',
      sortable: true,
      type: 'number',
      format: (value) => Number(value).toLocaleString()
    },
    {
      key: 'conversions',
      label: 'Conversões',
      sortable: true,
      type: 'number'
    },
    {
      key: 'revenue',
      label: 'Receita',
      sortable: true,
      type: 'number',
      format: (value) => `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
    },
    {
      key: 'roi',
      label: 'ROI',
      sortable: true,
      type: 'number',
      positiveNegative: true,
      threshold: 0,
      showTrendIcon: true,
      format: (value) => `${Number(value).toFixed(1)}%`
    },
    {
      key: 'cpa',
      label: 'CPA',
      sortable: true,
      type: 'number',
      format: (value) => `R$ ${Number(value).toFixed(2)}`
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      type: 'tag',
      tagColors: {
        'active': 'bg-green-100 text-green-800',
        'paused': 'bg-yellow-100 text-yellow-800',
        'ended': 'bg-gray-100 text-gray-800'
      }
    }
  ];

  // Ações para tabela de campanhas
  const campaignTableActions = [
    {
      label: 'Ver detalhes',
      icon: <Eye size={16} />,
      className: 'text-blue-600 hover:bg-blue-100',
      onClick: (campaign) => console.log('Ver detalhes:', campaign)
    },
    {
      label: 'Editar',
      icon: <Edit size={16} />,
      className: 'text-amber-600 hover:bg-amber-100',
      onClick: handleEdit
    },
    {
      label: 'Excluir',
      icon: <Trash2 size={16} />,
      className: 'text-red-600 hover:bg-red-100',
      onClick: (campaign) => handleRemoveCampaign(campaign.id)
    }
  ];

  return (
    <div className="page-container">
      {/* Cabeçalho com filtros e controles */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="page-title mb-1">Controle de Tráfego Pago</h1>
          <p className="text-gray-600">Gerencie suas campanhas de marketing e analise resultados</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Navegação por Tabs */}
          <div className="flex bg-white rounded-lg shadow-sm border border-gray-200">
            <button
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'dashboard'
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setActiveTab('dashboard')}
            >
              Dashboard
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'campaigns'
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setActiveTab('campaigns')}
            >
              Campanhas
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'calculator'
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setActiveTab('calculator')}
            >
              Calculadora
            </button>
          </div>

          {/* Seletor de período */}
          <div className="flex items-center bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-3 py-2 border-r border-gray-200">
              <Calendar size={18} className="text-gray-500" />
            </div>
            <select
              className="py-2 px-3 border-none focus:ring-0 text-sm text-gray-700"
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <option value="7d">Últimos 7 dias</option>
              <option value="30d">Últimos 30 dias</option>
              <option value="90d">Últimos 90 dias</option>
              <option value="year">Este ano</option>
              <option value="all">Todo o período</option>
            </select>
          </div>

          {/* Seletor de canal */}
          <div className="flex items-center bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-3 py-2 border-r border-gray-200">
              <Filter size={18} className="text-gray-500" />
            </div>
            <select
              className="py-2 px-3 border-none focus:ring-0 text-sm text-gray-700"
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value)}
            >
              <option value="all">Todos os canais</option>
              <option value="facebook">Facebook</option>
              <option value="google">Google</option>
              <option value="tiktok">TikTok</option>
              <option value="taboola">Taboola</option>
            </select>
          </div>

          {/* Botão de atualizar */}
          <button
            className="p-2 rounded-lg text-gray-500 bg-white border border-gray-200 shadow-sm hover:bg-gray-50"
            onClick={refreshCampaigns}
            title="Atualizar dados"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Dashboard de Marketing */}
      {activeTab === 'dashboard' && (
  <div className="space-y-6">
    {/* KPIs */}
    <KpiCardGroup metrics={kpiMetrics} layout="grid-compact" />

    {/* Insights Automáticos */}
    {showInsights && campaignInsights.length > 0 && (
      <div className="card">
        <div className="card-header">
          <h2 className="card-title flex items-center">
            <Activity className="h-5 w-5 text-blue-600 mr-2" />
            Insights Automáticos
          </h2>
          <button
            className="text-sm text-gray-500 hover:text-gray-700"
            onClick={() => setShowInsights(false)}
          >
            Ocultar
          </button>
        </div>
        <div className="p-6">
          <InsightGenerator
            data={campaignData}
            previousData={[]} // Pode ser preenchido com histórico real futuramente
            metrics={[
              { key: 'cost', label: 'Investimento', higherIsBetter: false },
              { key: 'revenue', label: 'Receita', higherIsBetter: true },
              { key: 'roi', label: 'ROI', higherIsBetter: true },
              { key: 'cpa', label: 'CPA', higherIsBetter: false, inverseTrend: true }
            ]}
            targets={{ roi: 200, cpa: 25 }}
            significanceThreshold={10}
            period="no período selecionado"
          />
        </div>
      </div>
    )}

    {/* Tendência de Performance */}
    <div className="card overflow-hidden">
      <MetricChart {...dailyTrendConfig} />
    </div>

    {/* Performance por Canal + Distribuição */}
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
      <div className="md:col-span-2">
        <div className="card overflow-hidden">
          <MetricChart {...channelPerformanceConfig} />
        </div>
      </div>
      <div className="h-full">
        <div className="card h-full overflow-hidden">
          <div className="card-header">
            <h2 className="card-title flex items-center">
              <PieChart className="h-5 w-5 text-blue-600 mr-2" />
              Distribuição de Investimento
            </h2>
          </div>
          <div className="p-6 h-80">
            <DistributionPie
              data={channelDistributionData}
              nameKey="name"
              dataKey="value"
              donut
              centerLabel="Total"
              centerValue={`R$ ${formatNumber(channelSummary.cost?.value || 0)}`}
              valueFormatter={(value) => `R$ ${value.toLocaleString('pt-BR')}`}
            />
          </div>
        </div>
      </div>
    </div>

    {/* Análise ROI vs CPA */}
    <div className="card overflow-hidden">
      <div className="card-header">
        <h2 className="card-title flex items-center">
          <BarChart2 className="h-5 w-5 text-blue-600 mr-2" />
          Análise ROI vs. CPA por Canal
        </h2>
      </div>
      <div className="p-6 h-80">
        <ScatterAnalysis
          data={channelData}
          xAxisDataKey="cpa"
          yAxisDataKey="roi"
          sizeKey="cost"
          nameKey="channel"
          xAxisLabel="CPA (R$)"
          yAxisLabel="ROI (%)"
          xAxisFormatter={(v) => `R$ ${v.toFixed(2)}`}
          yAxisFormatter={(v) => `${v.toFixed(1)}%`}
          quadrantLabels={[
            'Alto ROI / Baixo CPA (Ideal)',
            'Alto ROI / Alto CPA',
            'Baixo ROI / Alto CPA (Problema)',
            'Baixo ROI / Baixo CPA'
          ]}
          showQuadrants
        />
      </div>
    </div>

    {/* Top Campanhas */}
    <div className="card overflow-hidden">
      <div className="card-header">
        <h2 className="card-title flex items-center">
          <BarChart2 className="h-5 w-5 text-blue-600 mr-2" />
          Top Campanhas por ROI
        </h2>
        <button
          className="btn-primary text-sm flex items-center"
          onClick={() => setActiveTab('campaigns')}
        >
          <Eye className="h-4 w-4 mr-2" />
          Ver Todas
        </button>
      </div>
      <div className="p-6 h-80">
        <PerformanceBar
          data={campaignData.sort((a, b) => b.roi - a.roi).slice(0, 10)}
          dataKey="roi"
          nameKey="name"
          orientation="horizontal"
          showValues
          valueFormatter={(v) => `${Number(v).toFixed(1)}%`}
          threshold={0}
        />
      </div>
    </div>
  </div>
)}

      {/* Gestão de Campanhas */}
      {activeTab === 'campaigns' && (
        <>
          {/* Formulário para adicionar ou editar campanha */}
          <div className="card mb-6">
            <div className="card-header">
              <h2 className="card-title flex items-center">
                <Plus className="h-5 w-5 text-blue-600 mr-2" />
                {editingCampaign ? 'Editar Campanha' : 'Adicionar Nova Campanha'}
              </h2>
              {editingCampaign && (
                <button
                  className="btn-outline text-sm"
                  onClick={handleCancelEdit}
                >
                  Cancelar Edição
                </button>
              )}
            </div>
            <div className="p-6">
              <TrafficForm
                onAddCampaign={handleAddCampaign}
                onSaveEdit={handleSaveEdit}
                onCancelEdit={handleCancelEdit}
                editingCampaign={editingCampaign || null}
              />
            </div>
          </div>

          {/* Tabela de campanhas com pesquisa, filtros, ordenação */}
          <MetricTable
            data={campaigns}
            columns={campaignTableColumns}
            title="Histórico de Campanhas"
            enableSorting={true}
            enableFiltering={true}
            pagination={{ enabled: true, pageSize: 10 }}
            conditionalStyles={{
              profitable: {
                condition: row => row.roi > 0,
                className: 'bg-green-50'
              },
              unprofitable: {
                condition: row => row.roi < 0,
                className: 'bg-red-50'
              }
            }}
            actions={campaignTableActions}
            idField="id"
          />
        </>
      )}

      {/* Calculadora de ROI */}
      {activeTab === 'calculator' && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title flex items-center">
              <BarChart2 className="h-5 w-5 text-blue-600 mr-2" />
              Calculadora de ROI
            </h2>
          </div>
          <div className="p-6">
            <div className="bg-blue-50 p-4 rounded-lg mb-6 flex items-start">
              <HelpCircle className="text-blue-600 mr-3 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-blue-700 font-medium">Como usar</h3>
                <p className="text-blue-600 text-sm">
                  Esta calculadora permite simular diferentes cenários de investimento e conversão para estimar o ROI e outros indicadores importantes de suas campanhas.
                </p>
              </div>
            </div>
            <ROICalculator />
          </div>
        </div>
      )}
    </div>
  );
};

export default TrafegoMarketing;
